import chalk from 'chalk';
import { logError, Logger } from '~/util/logger';

import {
  Block,
  BlockActionType,
  BlockVersion,
  GetBlockLogsParams,
  PushBlockParams,
} from '~/models/Contensis';
import ContensisRepository from '~/repositories/ContensisRepository';
import mapErrorToObject from '~/transformations/mapAppErrorToObject';

class BlocksService {
  private contensis: ContensisRepository;
  private source: ContensisRepository;
  error: AppError | null = null;
  raw: Block[] = [];

  constructor(contensis: ContensisRepository, source: ContensisRepository) {
    this.contensis = contensis;
    this.source = source;
  }

  GetBlocks = async (): Promise<[AppError | null, Block[] | undefined]> => {
    Logger.info(
      `[blocks] ${this.contensis.alias} | Getting blocks in ${this.contensis.projectId}`
    );
    const [err, blocks] = await this.contensis.repo.GetBlocks<Block[]>();

    if (err) {
      logError(err);
      return [mapErrorToObject(err), undefined];
    }

    this.raw = blocks || [];
    return [err, this.raw];
  };

  GetBlockVersions = async (
    blockId: string,
    branchId?: string,
    version?: string
  ): Promise<[AppError | null, BlockVersion[] | undefined]> => {
    Logger.info(
      `[blocks] ${this.contensis.alias}:${this.contensis.projectId} | Getting block ${blockId} for branch ${branchId}`
    );
    const [err, blockVersion] = await this.contensis.repo.GetBlockVersions(
      blockId,
      branchId,
      version
    );

    if (err) logError(err);
    return [
      err,
      blockVersion &&
        ('items' in blockVersion ? blockVersion.items : [blockVersion]),
    ];
  };

  PushBlockVersion = async (
    block: PushBlockParams
  ): Promise<[AppError | null, BlockVersion | undefined]> => {
    Logger.info(
      `[blocks] ${this.contensis.alias}:${this.contensis.projectId} | Pushing block ${block.id} in branch ${block.source.branch} ${block.makeLive ? 'and making it live' : block.release ? 'and releasing' : ''}`
    );
    const [err, pushed] = await this.contensis.repo.PushBlock(block);

    if (err) logError(err);
    return [err, pushed];
  };

  BlockAction = async (
    blockId: string,
    actionType: BlockActionType,
    version: string
  ): Promise<[AppError | null, BlockVersion | undefined]> => {
    Logger.info(
      `[blocks] ${this.contensis.alias}:${this.contensis.projectId} | Requesting action ${actionType} on block ${blockId} v${version}`
    );
    const [err, response] = await this.contensis.repo.BlockAction(
      blockId,
      actionType,
      version
    );

    if (err) logError(err);
    return [err, response];
  };

  GetBlockLogs = async (
    params: GetBlockLogsParams
  ): Promise<[AppError | null, string | undefined]> => {
    if (params.dataCenter) {
      Logger.info(
        `[blocks] ${this.contensis.alias}:${this.contensis.projectId} | Getting logs in ${params.dataCenter} datacentre`
      );
      const [err, blockLogs] = await this.contensis.repo.GetBlockLogs(params);
      if (err) logError(err);
      return [err, blockLogs];
    } else {
      // Look to all dataCenters for logs and interpolate the results
      const promises: Promise<
        [Error | null, string | undefined, Response | undefined]
      >[] = [];

      const dataCenters = [
        'hq',
        'manchester',
        'london',
      ] as GetBlockLogsParams['dataCenter'][];

      for (const dataCenter of dataCenters) {
        promises.push(
          this.contensis.repo.GetBlockLogs({
            ...params,
            dataCenter,
          })
        );
      }

      // The promises will always resolve due to error handling in GetBlockLogs repo method
      const settled = await Promise.all(promises);
      const removeTrailingNewline = (logs: string) =>
        logs.endsWith('\n') ? logs.slice(0, logs.length - 1) : logs;
      const abbreviateDataCenter = (dc: GetBlockLogsParams['dataCenter']) => {
        if (dc === 'hq') return chalk.green('h');
        if (dc === 'manchester') return chalk.blue('m');
        if (dc === 'london') return chalk.red('l');
        return '?';
      };

      type SplitLogLine = {
        dc: GetBlockLogsParams['dataCenter'];
        dt: Date;
        log: string;
      };
      const logLines: SplitLogLine[] = [];
      const errors: Error[] = [];
      settled.forEach((result, idx) => {
        const [err, blockLogs] = result;
        if (err) errors.push(err);
        if (blockLogs) {
          const splitLogs = removeTrailingNewline(blockLogs)
            .split('\n')
            .map((line) => {
              const dt = new Date(line.split(' ')[0]) || new Date();
              const log = line.split(' ').slice(1).join(' ');
              return { dc: dataCenters[idx], dt, log };
            });
          logLines.push(...splitLogs);
        }
      });

      const renderLines: string[] = [];
      // Sort logLines by date and render output
      for (const line of logLines.sort(
        (a, b) => a.dt.valueOf() - b.dt.valueOf()
      )) {
        renderLines.push(
          `${abbreviateDataCenter(line.dc)} ${line.dt.toISOString()} ${
            line.log
          }`
        );
      }

      if (renderLines.length === 0 && errors.length > 1)
        return [errors[0], undefined];
      else return [null, renderLines.join('\n')];
    }
  };

  BuildBlocksToMigrate = () => {
    throw new Error(`BuildBlocksToMigrate is not implemented `);
  };
}

export default BlocksService;
